/**
 * Búsqueda en OpenAlex (> 250 M de trabajos de todas las disciplinas).
 *
 * https://docs.openalex.org — la búsqueda se restringe a título + abstract (como en
 * Europe PMC) y solo a trabajos con abstract, no retractados. Muchas editoriales han
 * retirado sus abstracts de OpenAlex, así que la fuente aporta menos de lo que su
 * número de resultados sugiere.
 */

import { type Paper, type SearchPage, type SortOrder, normalizeDoi, paperUrl } from "../core/models";
import { type FetchFn, timing } from "./config";
import { t } from "../core/i18n";
import { RETRACTED_TYPE } from "../core/reliability";
import { SearchError, stripHtml } from "./europepmc";

// Campos mínimos: la lista de autores (con instituciones) triplica el tamaño de la respuesta
// y parsearla cuesta CPU, que en el plan gratuito de Workers está limitada a 10 ms
// Sin authorships: traen instituciones y afiliaciones y disparan la CPU del Worker
const SELECT = "id,doi,title,publication_year,primary_location,type,cited_by_count,abstract_inverted_index,ids,keywords,is_retracted,open_access";
const TYPES = "article|review|preprint";

interface Work {
  id?: string;
  doi?: string | null;
  title?: string | null;
  publication_year?: number | null;
  type?: string | null;
  cited_by_count?: number | null;
  abstract_inverted_index?: Record<string, number[]> | null;
  ids?: { pmid?: string; pmcid?: string } | null;
  primary_location?: { source?: { display_name?: string | null } | null; license?: string | null } | null;
  keywords?: { display_name?: string }[] | null;
  is_retracted?: boolean | null;
  open_access?: { oa_url?: string | null } | null;
}

// Un abstract real tiene 150-350 palabras; OpenAlex a veces guarda como "abstract" trozos
// del texto completo (miles de palabras) y reconstruirlos entero se come la CPU del Worker
export const MAX_ABSTRACT_WORDS = 500;

/** OpenAlex guarda el abstract como índice invertido {palabra: [posiciones]}. */
export function invertedToText(index: Record<string, number[]>, maxWords = MAX_ABSTRACT_WORDS): string {
  const words: string[] = new Array(maxWords);
  let last = -1;
  for (const word in index) {
    for (const pos of index[word]!) {
      if (pos < maxWords) {
        words[pos] = word;
        if (pos > last) last = pos;
      }
    }
  }
  let text = "";
  for (let i = 0; i <= last; i++) {
    const w = words[i];
    if (w !== undefined) text += text ? ` ${w}` : w;
  }
  return text;
}

const lastSegment = (url?: string | null) => url?.split("/").filter(Boolean).at(-1) ?? null;

// Un abstract de 500 palabras tiene unas 250 distintas; con muchas más, es texto completo
// guardado como abstract: no sirve como abstract y reconstruirlo cuesta CPU
export const MAX_DISTINCT_WORDS = 1200;

export function parseWork(w: Work): Paper | null {
  if (!w.id || !w.title || !w.abstract_inverted_index) return null;
  if (Object.keys(w.abstract_inverted_index).length > MAX_DISTINCT_WORDS) return null;
  const abstract = stripHtml(invertedToText(w.abstract_inverted_index));
  if (!abstract) return null;
  const paper: Paper = {
    source_id: `OA:${lastSegment(w.id)}`,
    source: "openalex",
    title: stripHtml(w.title),
    abstract,
    doi: normalizeDoi(w.doi),
    pmid: lastSegment(w.ids?.pmid),
    pmcid: lastSegment(w.ids?.pmcid),
    year: w.publication_year ?? null,
    journal: w.primary_location?.source?.display_name ?? null,
    license: w.primary_location?.license ?? null,
    authors: null,
    publication_types: [...(w.type ? [w.type] : []), ...(w.is_retracted ? [RETRACTED_TYPE] : [])],
    cited_by_count: w.cited_by_count ?? null,
    oa_url: w.open_access?.oa_url ?? null,
    keywords: (w.keywords ?? []).map((k) => k.display_name).filter((x): x is string => !!x),
  };
  paper.url = paperUrl(paper) ?? w.id;
  return paper;
}

export interface OpenAlexRequest {
  query: string;
  sort: SortOrder;
  cursor?: string;
  pageSize: number;
  titleAbsOnly: boolean;
}

/** Presupuesto agotado (de la clave o del acceso sin clave). */
export class BudgetError extends SearchError {}

/**
 * La API key tiene un presupuesto diario (1 $ gratis, se renueva a medianoche UTC). Agotado,
 * OpenAlex responde 429 con Retry-After de horas: reintentar no sirve y cada intento cuenta.
 */
export function budgetCheck(resp: Response): void {
  if (resp.status !== 429) return;
  const wait = Number(resp.headers.get("retry-after") ?? 0);
  if (resp.headers.get("x-ratelimit-remaining-usd") === "0" || wait > 60) {
    throw new BudgetError("OpenAlex: se ha agotado el presupuesto diario de la API (se renueva a medianoche UTC)");
  }
}

export const ANONYMOUS_NOTICE = t("es", "api.openalex_anonymous");

export interface OpenAlexHooks {
  // Coste de cada llamada que respondió (cabecera X-RateLimit-Cost-USD) y si fue sin clave
  onCall?: (usd: number, anonymous: boolean) => void;
  // La clave se quedó sin presupuesto (se sigue sin ella)
  onKeyExhausted?: () => void;
}

/**
 * GET a OpenAlex con reintentos. Si la clave se queda sin presupuesto, se repite la llamada sin
 * ella (acceso anónimo oficial, ~0,10 $/día); si también está agotado, BudgetError.
 */
export async function openalexGet(
  fetchFn: FetchFn,
  base: string,
  params: URLSearchParams,
  retries: number,
  hooks: OpenAlexHooks = {},
): Promise<{ text: string; anonymous: boolean }> {
  let problem = "";
  let fellBack = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt) await timing.sleep(500 * 2 ** (attempt - 1));
    const anonymous = !params.has("api_key");
    let resp: Response;
    try {
      resp = await fetchFn(`${base}?${params}`);
    } catch (e) {
      problem = `error de red: ${e}`;
      continue;
    }
    if (resp.ok) hooks.onCall?.(Number(resp.headers.get("x-ratelimit-cost-usd")) || 0, anonymous);
    try {
      budgetCheck(resp);
    } catch (e) {
      if (anonymous || fellBack) throw e;
      params.delete("api_key");
      fellBack = true;
      hooks.onKeyExhausted?.();
      attempt--;
      continue;
    }
    if (resp.status === 429 || resp.status >= 500) {
      problem = `HTTP ${resp.status}`;
      continue;
    }
    const text = await resp.text();
    if (!resp.ok) throw new SearchError(`OpenAlex devolvió HTTP ${resp.status}: ${text.slice(0, 200)}`);
    return { text, anonymous };
  }
  throw new SearchError(`OpenAlex no respondió bien tras ${retries + 1} intentos (${problem})`);
}

/**
 * Una página. `sort: "default"` = relevancia (necesita el parámetro `search`, que busca
 * también en el texto completo, por eso se combina con el filtro de título + abstract);
 * `"cited"` = más citados primero.
 */
export async function searchPage(
  fetchFn: FetchFn,
  url: string,
  apiKey: string,
  req: OpenAlexRequest,
  retries = 4,
  hooks: OpenAlexHooks = {},
): Promise<SearchPage> {
  // En los filtros de OpenAlex la coma separa filtros: no puede aparecer en la consulta
  const query = req.query.replace(/,/g, " ");
  const filters = [`type:${TYPES}`, "is_retracted:false", "has_abstract:true"];
  if (req.titleAbsOnly) filters.unshift(`title_and_abstract.search:${query}`);
  const cursor = req.cursor || "*";
  const params = new URLSearchParams({
    filter: filters.join(","),
    select: SELECT,
    per_page: String(req.pageSize),
    cursor,
  });
  if (req.sort === "cited") {
    params.set("sort", "cited_by_count:desc");
    if (!req.titleAbsOnly) params.set("search", query);
  } else {
    params.set("search", query);
    params.set("sort", "relevance_score:desc");
  }
  if (apiKey) params.set("api_key", apiKey);

  const { text, anonymous } = await openalexGet(fetchFn, url, params, retries, hooks);
  let data: { meta?: { count?: number; next_cursor?: string | null }; results?: Work[] };
  try {
    data = JSON.parse(text);
  } catch {
    throw new SearchError("OpenAlex devolvió una respuesta no JSON");
  }
  const results = data.results ?? [];
  const next = data.meta?.next_cursor;
  return {
    papers: results.map(parseWork).filter((p): p is Paper => p !== null),
    hit_count: data.meta?.count ?? 0,
    next_cursor: results.length && next && next !== cursor ? next : null,
    ...(anonymous && apiKey ? { notice: ANONYMOUS_NOTICE } : {}),
  };
}

export interface DoiInfo {
  oa_url: string | null;
  retracted: boolean;
}

// OpenAlex admite hasta 50 valores por filtro con «|»
const DOI_BATCH = 50;

/**
 * Acceso abierto (datos de Unpaywall) y retractaciones (Retraction Watch/Crossref) de una lista de
 * DOIs, en una llamada por cada 50: es un filtro sin texto, 1 crédito (0,0001 $) por llamada.
 */
export async function lookupDois(
  fetchFn: FetchFn,
  url: string,
  apiKey: string,
  dois: string[],
  hooks: OpenAlexHooks = {},
): Promise<Map<string, DoiInfo>> {
  const out = new Map<string, DoiInfo>();
  const unique = [...new Set(dois.map((d) => normalizeDoi(d)).filter((d): d is string => !!d && !d.includes("|") && !d.includes(",")))];
  for (let i = 0; i < unique.length; i += DOI_BATCH) {
    const params = new URLSearchParams({
      filter: `doi:${unique.slice(i, i + DOI_BATCH).join("|")}`,
      select: "doi,is_retracted,open_access",
      per_page: String(DOI_BATCH),
    });
    if (apiKey) params.set("api_key", apiKey);
    const { text } = await openalexGet(fetchFn, url, params, 1, hooks);
    const data = JSON.parse(text) as { results?: { doi?: string | null; is_retracted?: boolean; open_access?: { oa_url?: string | null } }[] };
    for (const w of data.results ?? []) {
      const doi = normalizeDoi(w.doi);
      if (doi) out.set(doi, { oa_url: w.open_access?.oa_url ?? null, retracted: w.is_retracted === true });
    }
  }
  return out;
}
